import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, catchError, from, map, of, race, switchMap, tap, timer } from 'rxjs';
import { REQUEST } from '../../express.tokens';
import { GROWTHBOOK } from '../growthbook';
import { BrowserService } from './browser.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class GrowthBookService {
  private growthBook = inject(GROWTHBOOK);
  private browserService = inject(BrowserService);
  private http = inject(HttpClient);
  private loader = inject(ObservableDataLoaderService);
  private platformId = inject(PLATFORM_ID);
  private storage = inject(StorageService);
  private req = inject<Request>(REQUEST, { optional: true });

  public loaded$ = new BehaviorSubject<boolean>(false);

  private getLocalOverrides(): Record<string, boolean> {
    if (isPlatformServer(this.platformId)) {
      const searchString = this.req?.url.split('?')[1];
      if (!searchString) return {};
      const queryParams =
        searchString.split('&').reduce(
          (acc, curr) => {
            const [key, value] = curr.split('=');
            acc[key] = value;
            return acc;
          },
          {} as Record<string, string>
        ) || {};

      if (!queryParams.ff) return {};

      return queryParams.ff.split(',').reduce(
        (acc, curr) => {
          const [key, value] = curr.split(':');
          acc[key] = value === 'true';
          return acc;
        },
        {} as Record<string, boolean>
      );
    }
    const fromStorage = this.storage.get<Record<string, boolean>>('local-feature-flag-overrides') || {};
    // ff=override1:true,override2:false
    const fromUrl =
      new URLSearchParams(window.location.search)
        .get('ff')
        ?.split(',')
        .reduce(
          (acc, curr) => {
            const [key, value] = curr.split(':');
            acc[key] = value === 'true';
            return acc;
          },
          {} as Record<string, boolean>
        ) || {};
    return { ...fromStorage, ...fromUrl };
  }

  constructor() {
    if (isPlatformServer(this.platformId)) return;
    window['overrideFeatureFlag'] = (key: string, value: boolean) => {
      const localOverrides = this.getLocalOverrides();
      localOverrides[key] = value;
      this.storage.set('local-feature-flag-overrides', localOverrides);
      window.location.reload();
    };

    window['clearFeatureFlagOverrides'] = () => {
      this.storage.remove('local-feature-flag-overrides');
      window.location.reload();
    };
  }

  public load = () =>
    this.loader.get('growthbook', () => {
      return race([
        this.http.get(`${this.browserService.getCorrectPicflowUrl(true)}/internal-api/features`).pipe(
          catchError(() => {
            console.warn('Failed to load feature flags');
            return of(null);
          })
        ),
        timer(2000).pipe(tap(() => console.warn('GrowthBook timeout'))),
      ]).pipe(
        switchMap(resp => {
          if (!resp) return of(null);
          return from(this.growthBook.setPayload(resp)).pipe(map(() => true));
        }),
        tap(() => {
          const localOverrides = new Map(Object.entries(this.getLocalOverrides()));
          if (localOverrides.size > 0) {
            this.growthBook.setForcedFeatures(localOverrides);
            console.log('Applied local feature flag overrides:', localOverrides);
          }
          this.loaded$.next(true);
        })
      );
    });
}
